import { useMutation } from '@apollo/client';
import { DELETE_COMMUNITY_POLICY, GET_COMMUNITY_POLICIES } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface DeleteCommunityPolicyResponse {
  deletePolicy: boolean;
}

export function useDeleteCommunityPolicy(communityId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deletePolicyMutation, { loading }] = useMutation<DeleteCommunityPolicyResponse>(DELETE_COMMUNITY_POLICY, {
    refetchQueries: communityId ? [{ query: GET_COMMUNITY_POLICIES, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deletePolicy = async (communityId: string, policyId: string): Promise<boolean> => {
    try {
      track('DeleteCommunityPolicyInitiated', {
        communityId,
        policyId,
      });

      setErrors(null);
      await deletePolicyMutation({
        variables: {
          communityId,
          input: { policyId },
        },
      });

      track('DeleteCommunityPolicySucceeded', {
        communityId,
        policyId,
      });

      return true;
    } catch (error) {
      track('DeleteCommunityPolicyFailed', {
        communityId,
        policyId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deletePolicy,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteCommunityPolicy;
